import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePageContent } from "@/hooks/usePageContent";
import { SectionHidden } from "@/components/common";
import { Button } from "@/components/ui/button";
import { ArrowRight, AlertTriangle, CalendarDays, Zap, Eye, EyeOff, Settings2, Cake, PartyPopper, ChevronRight, Megaphone, Award, ShieldCheck, GraduationCap } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import LeaveModule from "@/components/LeaveModule";
import TeamOffStrip from "@/components/TeamOffStrip";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const TONE = {
  blue: "border-l-[#2EA3FD]", teal: "border-l-[#36776B]", amber: "border-l-amber-400", navy: "border-l-[#0B1A2F]",
};
const TOP_TONE = {
  blue: "border-t-[#2EA3FD]", teal: "border-t-[#36776B]", amber: "border-t-amber-400", navy: "border-t-[#0B1A2F]",
};
const ATT_TINT = {
  overdue: "border-l-amber-400 bg-amber-50/70", due: "border-l-[#36776B] bg-emerald-50/60", action: "border-l-[#2EA3FD] bg-sky-50/60",
};

function DateChip({ date }) {
  let day = "", mon = "";
  try { const dt = new Date(date); day = dt.getDate(); mon = dt.toLocaleString("en-GB", { month: "short" }).toUpperCase(); } catch { /* noop */ }
  return (
    <div className="flex flex-col items-center justify-center h-11 w-11 rounded-lg bg-slate-100 text-[#0B1A2F] shrink-0">
      <span className="text-sm font-bold leading-none">{day}</span>
      <span className="text-[9px] font-semibold text-slate-500">{mon}</span>
    </div>
  );
}
function SensitiveStat({ value }) {
  const [shown, setShown] = useState(false);
  return (
    <span className="mt-1 flex items-center gap-2">
      <span className={`text-3xl font-bold text-[#0B1A2F] transition-[filter] duration-200 ${shown ? "" : "blur-[7px] group-hover:blur-0 select-none"}`} data-testid="salary-cost-value">{value}</span>
      <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShown((s) => !s); }}
        className="text-slate-400 hover:text-[#2EA3FD] transition-colors shrink-0" data-testid="salary-cost-toggle"
        aria-label={shown ? "Hide annual salary cost" : "Reveal annual salary cost"} title={shown ? "Hide" : "Reveal"}>
        {shown ? <EyeOff size={16} /> : <Eye size={16} />}
      </button>
    </span>
  );
}
function greetWord() {
  const h = new Date().getHours();
  return h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening";
}

// Catalogue of cards / quick actions available per role (mirrors backend /api/home).
// Used by the super-admin per-role visibility toggles.
const ROLE_CATALOG = {
  employee: {
    label: "Employee",
    cards: ["Holiday remaining", "Holiday booked", "Next planned leave", "My tasks"],
    quick: ["My profile", "My documents", "My training", "My Total Reward"],
  },
  manager: {
    label: "Manager",
    cards: ["Absent today", "Awaiting approval", "Reviews due (30d)", "Team size", "Candidates to review"],
    quick: ["Review requests", "Team calendar", "Start appraisal", "Team training"],
  },
  hr_admin: {
    label: "HR administrator",
    cards: ["Headcount", "Open vacancies", "Upcoming starters", "Awaiting approval"],
    quick: ["Add employee", "Create vacancy", "Add policy", "Run HR report"],
  },
  admin: {
    label: "Super admin",
    cards: ["Headcount", "Open vacancies", "Upcoming starters", "Annual salary cost"],
    quick: ["Create vacancy", "Add employee", "Run report", "Manage users", "Settings"],
  },
};

export default function Dashboard() {
  const { user } = useAuth();
  const pc = usePageContent();
  const hh = pc?.home?.headings || {};
  const ann = pc?.home?.announcements;
  const myLeave = pc?.home?.my_leave;
  const [d, setD] = useState(null);
  const [asRole, setAsRole] = useState("self");
  const [cfg, setCfg] = useState(null);
  const [showCfg, setShowCfg] = useState(false);
  const [content, setContent] = useState(null);
  const [signItem, setSignItem] = useState(null);
  const [signName, setSignName] = useState("");
  const reloadHome = () => { const qs = asRole && asRole !== "self" ? `?as_role=${asRole}` : ""; api.get(`/home${qs}`).then((r) => setD(r.data)).catch(() => {}); };
  const submitSign = async () => {
    if (!signName.trim()) { toast.error("Type your full name to sign"); return; }
    try { await api.post(`/training/${signItem.tid}/sign`, { signed_name: signName.trim() }); toast.success("Training signed"); setSignItem(null); setSignName(""); reloadHome(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not sign"); }
  };
  useEffect(() => {
    const qs = asRole && asRole !== "self" ? `?as_role=${asRole}` : "";
    api.get(`/home${qs}`).then((r) => setD(r.data)).catch(() => setD({ cards: [], needs_attention: [], upcoming: [], quick_actions: [] }));
    api.get("/home-content").then((r) => setContent(r.data)).catch(() => {});
  }, [asRole]);
  const openCfg = () => { api.get("/home-config").then((r) => { setCfg({ ...r.data, hidden: r.data.hidden || {} }); setShowCfg(true); }).catch(() => {}); };
  const saveCfg = async () => {
    try { await api.put("/home-config", { review_due_days: Number(cfg.review_due_days), training_expiry_days: Number(cfg.training_expiry_days), ack_reminder_days: Number(cfg.ack_reminder_days), show_birthdays: !!cfg.show_birthdays, hidden: cfg.hidden || {} }); toast.success("Homepage settings saved"); setShowCfg(false); setAsRole((r) => r); }
    catch { toast.error("Could not save"); }
  };
  const isHidden = (role, type, label) => (cfg?.hidden?.[role]?.[type] || []).includes(label);
  const toggleHidden = (role, type, label) => {
    setCfg((c) => {
      const hidden = { ...(c.hidden || {}) };
      const roleCfg = { ...(hidden[role] || {}) };
      const list = new Set(roleCfg[type] || []);
      list.has(label) ? list.delete(label) : list.add(label);
      roleCfg[type] = [...list];
      hidden[role] = roleCfg;
      return { ...c, hidden };
    });
  };

  if (!d) return <div className="text-slate-400 text-sm py-10" data-testid="home-loading">Loading your homepage…</div>;
  const first = (d.name || user?.name || "").split(" ")[0];
  const isEmp = d.viewing_as ? d.viewing_as === "employee" : user?.role === "employee";
  const empId = user?.employee_id;
  const celebrations = (d.upcoming || []).filter((u) => u.type === "Birthday" || u.type === "Work anniversary");
  const upcomingRest = (d.upcoming || []).filter((u) => u.type !== "Birthday" && u.type !== "Work anniversary");

  return (
    <div className="space-y-8" data-testid="home-page">
      {d.can_preview && (
        <div className="flex items-center gap-3 flex-wrap bg-[#0B1A2F] text-white rounded-xl px-4 py-2.5" data-testid="preview-bar">
          <Eye size={15} className="text-[#2EA3FD]" />
          <span className="text-sm">Preview as</span>
          <Select value={asRole} onValueChange={setAsRole}>
            <SelectTrigger className="h-8 w-44 bg-white/10 border-white/20 text-white text-sm" data-testid="preview-role-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="self">My own homepage</SelectItem>
              <SelectItem value="employee">Employee</SelectItem>
              <SelectItem value="manager">Manager</SelectItem>
              <SelectItem value="hr_admin">HR administrator</SelectItem>
              <SelectItem value="read_only">Read-only adviser</SelectItem>
            </SelectContent>
          </Select>
          {d.viewing_as && <span className="text-xs text-slate-300">Showing the <strong className="text-white">{d.viewing_as.replace("_", " ")}</strong> layout — no real employee's restricted data is exposed.</span>}
          <button onClick={openCfg} className="ml-auto flex items-center gap-1.5 text-xs text-slate-200 hover:text-white" data-testid="home-config-btn"><Settings2 size={14} />Homepage settings</button>
        </div>
      )}
      {showCfg && cfg && (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 space-y-3" data-testid="home-config-panel">
          <h2 className="font-semibold text-[#0B1A2F]">Homepage settings</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div><label className="text-xs text-slate-500">Reviews "due soon" (days)</label><Input type="number" value={cfg.review_due_days} onChange={(e) => setCfg({ ...cfg, review_due_days: e.target.value })} data-testid="cfg-review-days" /></div>
            <div><label className="text-xs text-slate-500">Training expiry window (days)</label><Input type="number" value={cfg.training_expiry_days} onChange={(e) => setCfg({ ...cfg, training_expiry_days: e.target.value })} data-testid="cfg-training-days" /></div>
            <div><label className="text-xs text-slate-500">Ack reminder lead (days)</label><Input type="number" value={cfg.ack_reminder_days} onChange={(e) => setCfg({ ...cfg, ack_reminder_days: e.target.value })} data-testid="cfg-ack-days" /></div>
            <div className="flex items-end"><label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={!!cfg.show_birthdays} onChange={(e) => setCfg({ ...cfg, show_birthdays: e.target.checked })} data-testid="cfg-birthdays" />Show birthdays</label></div>
          </div>
          <div className="border-t border-slate-100 pt-3 space-y-4" data-testid="cfg-visibility">
            <div>
              <h3 className="text-sm font-semibold text-[#0B1A2F]">Card &amp; quick-action visibility</h3>
              <p className="text-xs text-slate-400">Untick to hide a card or quick action from that role's homepage.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.entries(ROLE_CATALOG).map(([role, meta]) => (
                <div key={role} className="border border-slate-100 rounded-lg p-3" data-testid={`cfg-role-${role}`}>
                  <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">{meta.label}</p>
                  <p className="text-[11px] text-slate-400 mb-1">Cards</p>
                  <div className="flex flex-wrap gap-x-4 gap-y-1.5 mb-3">
                    {meta.cards.map((label) => (
                      <label key={label} className="flex items-center gap-1.5 text-xs text-slate-600">
                        <input type="checkbox" checked={!isHidden(role, "cards", label)} onChange={() => toggleHidden(role, "cards", label)} data-testid={`cfg-card-${role}-${label.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`} />{label}
                      </label>
                    ))}
                  </div>
                  <p className="text-[11px] text-slate-400 mb-1">Quick actions</p>
                  <div className="flex flex-wrap gap-x-4 gap-y-1.5">
                    {meta.quick.map((label) => (
                      <label key={label} className="flex items-center gap-1.5 text-xs text-slate-600">
                        <input type="checkbox" checked={!isHidden(role, "quick", label)} onChange={() => toggleHidden(role, "quick", label)} data-testid={`cfg-quick-${role}-${label.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`} />{label}
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="flex gap-2"><Button className="bg-[#2EA3FD] hover:bg-[#1B8EE6]" onClick={saveCfg} data-testid="cfg-save">Save</Button><Button variant="outline" onClick={() => setShowCfg(false)}>Close</Button></div>
        </div>
      )}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#0B1A2F] via-[#0F2C50] to-[#2EA3FD] text-white p-6 sm:p-8" data-testid="home-hero"
        style={content?.hero_image_url ? { backgroundImage: `url(${content.hero_image_url})`, backgroundSize: "cover", backgroundPosition: "center" } : undefined}>
        {content?.hero_image_url && <div className="absolute inset-0 bg-gradient-to-br from-[#0B1A2F]/95 via-[#0F2C50]/90 to-[#2EA3FD]/80" />}
        <div className="absolute -right-16 -top-16 h-56 w-56 rounded-full bg-white/10" />
        <div className="absolute -right-4 bottom-0 h-32 w-32 rounded-full bg-[#36776B]/30" />
        <div className="relative flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-widest text-[#8FD0FF]" data-testid="home-date">{new Date().toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</p>
            <h1 className="text-3xl sm:text-4xl font-extrabold font-display mt-1" data-testid="home-greeting">{greetWord()}, {first}</h1>
            <p className="text-slate-200 mt-2 max-w-lg">{d.greeting_sub}</p>
            <p className="text-sm text-[#8FD0FF] mt-3 font-medium">{content?.hero_message || "Great people, brighter futures · #teamCASS"}</p>
            {content?.values?.length > 0 && <div className="flex flex-wrap gap-2 mt-3">{content.values.map((v, i) => <span key={i} className="text-xs rounded-full bg-white/15 px-2.5 py-1" data-testid={`hero-value-${i}`}>{v}</span>)}</div>}
          </div>
          {d.primary && <Button asChild className="bg-white text-[#0B1A2F] hover:bg-slate-100 h-11 px-6 text-base shrink-0"><Link to={d.primary.to} data-testid="home-primary-action">{d.primary.label}<ArrowRight size={17} className="ml-2" /></Link></Button>}
        </div>
      </div>

      {isEmp && empId && <LeaveModule empId={empId} />}
      {isEmp && empId && <TeamOffStrip empId={empId} />}

      {!isEmp && d.cards?.length > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="home-cards">
          {d.cards.map((c, i) => (
            <Link key={i} to={c.to} data-testid={`home-card-${i}`}
              className={`group bg-white rounded-xl border border-slate-200 border-t-4 ${TOP_TONE[c.tone] || TOP_TONE.blue} shadow-sm p-5 hover:shadow-md transition-shadow`}>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{c.label}</p>
              {/salary/i.test(c.label || "") ? <SensitiveStat value={c.value ?? 0} /> : <p className="text-3xl font-bold text-[#0B1A2F] mt-1">{c.value ?? 0}</p>}
              <p className="text-xs text-slate-400 mt-1">{c.sub}</p>
            </Link>
          ))}
        </div>
      )}

      <SectionHidden enabled={ann?.enabled} label="'What's new' announcements" />
      {ann?.enabled !== false && content?.announcements?.filter((a) => a.title || a.body).length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm" data-testid="home-announcements">
          <div className="px-5 pt-4 pb-2 flex items-center gap-2"><Megaphone size={16} className="text-[#2EA3FD]" /><h2 className="font-semibold text-[#0B1A2F]">{hh.whats_new || "What's new"}</h2></div>
          <div className="px-4 pb-4 space-y-2.5">
            {content.announcements.filter((a) => a.title || a.body).map((a, i) => (
              <div key={i} className="rounded-lg border-l-4 border-l-[#2EA3FD] bg-[#F5FAFF] px-4 py-3" data-testid={`announcement-${i}`}>
                {a.title && <p className="text-sm font-semibold text-[#0B1A2F]">{a.title}</p>}
                {a.body && <p className="text-xs text-slate-600 mt-0.5">{a.body}</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm" data-testid="home-attention">
            <div className="px-5 pt-4 pb-2">
              <h2 className="font-semibold text-[#0B1A2F] flex items-center gap-2"><AlertTriangle size={16} className="text-amber-500" />{isEmp ? "Your next steps" : (hh.needs_attention || "Needs attention")}{d.needs_attention.length > 0 && <span className="text-xs bg-rose-100 text-rose-700 rounded-full px-2 py-0.5">{d.needs_attention.length}</span>}</h2>
              <p className="text-xs text-slate-400 mt-0.5">Complete these actions by their due dates.</p>
            </div>
            <div className="px-4 pb-4 space-y-2.5">
              {d.needs_attention.length === 0 ? <p className="text-sm text-slate-400 px-1 py-4">Nothing needs your attention right now. 🎉</p> :
                d.needs_attention.map((a, i) => (
                  <div key={i} className={`flex items-center justify-between gap-3 rounded-lg border-l-4 px-4 py-3 ${ATT_TINT[a.status] || "border-l-slate-300 bg-slate-50"}`} data-testid={`attention-${i}`}>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-[#0B1A2F] truncate">{a.title}</p>
                      {a.desc && <p className="text-xs text-slate-500 truncate">{a.desc}</p>}
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      {a.due && <span className="text-xs text-slate-400 hidden sm:block">Due {fmtDate(a.due)}</span>}
                      {a.kind === "training_sign" && a.tid ? (
                        <Button size="sm" className="h-8 bg-[#2EA3FD] hover:bg-[#1B8EE6]" onClick={() => { setSignItem(a); setSignName(""); }} data-testid={`attention-sign-${i}`}>{a.action || "E-sign"}</Button>
                      ) : (
                        <Button asChild size="sm" variant="outline" className="h-8"><Link to={a.to} data-testid={`attention-action-${i}`}>{a.action || "Open"}</Link></Button>
                      )}
                    </div>
                  </div>
                ))}
            </div>
          </div>

          <Dialog open={!!signItem} onOpenChange={(o) => { if (!o) { setSignItem(null); setSignName(""); } }}>
            <DialogContent data-testid="home-sign-dialog">
              <DialogHeader><DialogTitle>{signItem?.title?.replace("E-sign training — ", "E-sign: ")}</DialogTitle></DialogHeader>
              {signItem?.details && signItem.details.replace(/<[^>]+>/g, "").trim() && (
                <div><p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">Training received</p><div className="text-sm text-slate-700 border border-slate-200 rounded-lg p-3 max-h-60 overflow-y-auto [&_ul]:list-disc [&_ul]:pl-5 [&_h2]:font-semibold [&_h2]:text-[#0B1A2F]" data-testid="home-sign-details" dangerouslySetInnerHTML={{ __html: signItem.details }} /></div>
              )}
              <p className="text-sm text-slate-600">{signItem?.statement || "I confirm I have received and understood this training."}</p>
              <div><p className="text-xs text-slate-500 mb-1">Type your full name to sign</p><Input value={signName} onChange={(e) => setSignName(e.target.value)} placeholder="Your full name" data-testid="home-sign-name-input" /></div>
              <DialogFooter>
                <Button variant="outline" onClick={() => { setSignItem(null); setSignName(""); }}>Cancel</Button>
                <Button className="bg-[#2EA3FD] hover:bg-[#1B8EE6]" onClick={submitSign} data-testid="home-sign-confirm-btn">Agree &amp; sign</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {!isEmp && myLeave?.enabled !== false && d.leave && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5" data-testid="home-my-leave">
              <h2 className="font-semibold text-[#0B1A2F]">My leave</h2>
              {d.leave.year_label && <p className="text-xs text-slate-400 mb-4">Leave year: {d.leave.year_label}</p>}
              <div className="flex flex-wrap items-end justify-between gap-4">
                <div className="flex gap-8">
                  <div><p className="text-xs text-slate-400">Allowance</p><p className="text-2xl font-bold text-[#0B1A2F]">{d.leave.allowance}h</p></div>
                  <div><p className="text-xs text-slate-400">Taken / booked</p><p className="text-2xl font-bold text-[#0B1A2F]">{d.leave.taken_booked}h</p></div>
                  <div><p className="text-xs text-slate-400">Available</p><p className="text-2xl font-bold text-[#2EA3FD]">{d.leave.available}h</p></div>
                </div>
                <div className="flex gap-2">
                  <Button asChild variant="outline"><Link to="/my-hr" data-testid="my-leave-view">View my leave</Link></Button>
                  <Button asChild className="bg-[#2EA3FD] hover:bg-[#1B8EE6]"><Link to="/requests" data-testid="my-leave-book">Book leave</Link></Button>
                </div>
              </div>
              <div className="mt-4 h-2.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-[#2EA3FD] transition-all" style={{ width: `${Math.min(100, Math.max(0, d.leave.pct))}%` }} data-testid="my-leave-bar" /></div>
              <p className="text-xs text-slate-400 mt-2">{d.leave.pct}% of your allowance is booked or taken.</p>
            </div>
          )}
        </div>

        <div className="space-y-6">
          {isEmp && (
            <Link to="/learning" data-testid="home-learning-card" className="block rounded-xl border border-slate-200 shadow-sm p-5 bg-gradient-to-br from-[#36776B] to-[#2b5f55] text-white hover:shadow-md transition-shadow">
              <div className="flex items-center gap-2"><GraduationCap size={18} /><h2 className="font-semibold">Learning</h2></div>
              <p className="text-sm text-white/85 mt-2">Grow your skills — view your courses, certificates and renewals.</p>
              <span className="inline-flex items-center gap-1.5 text-sm font-medium mt-3">Go to Learning<ArrowRight size={15} /></span>
            </Link>
          )}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm" data-testid="home-upcoming">
            <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2"><CalendarDays size={16} className="text-[#2EA3FD]" /><h2 className="font-semibold text-[#0B1A2F]">{hh.upcoming || "Upcoming"}</h2></div>
            <div className="divide-y divide-slate-50 max-h-80 overflow-y-auto">
              {upcomingRest.length === 0 ? <p className="text-sm text-slate-400 p-5">Nothing scheduled.</p> :
                upcomingRest.map((u, i) => (
                  <div key={i} className="flex items-start gap-3 px-5 py-3" data-testid={`upcoming-${i}`}>
                    <DateChip date={u.date} />
                    <div className="min-w-0 pt-0.5"><p className="text-sm font-semibold text-[#0B1A2F] truncate">{u.title}</p>{u.desc && <p className="text-xs text-slate-400 truncate">{u.desc}</p>}</div>
                  </div>
                ))}
            </div>
          </div>

          {celebrations.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm" data-testid="home-celebrations">
              <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2"><Cake size={16} className="text-pink-500" /><h2 className="font-semibold text-[#0B1A2F]">Birthdays &amp; anniversaries</h2></div>
              <div className="divide-y divide-slate-50 max-h-60 overflow-y-auto">
                {celebrations.map((u, i) => {
                  const milestone = u.type === "Work anniversary" && u.years && u.years % 5 === 0;
                  return (
                  <div key={i} className="flex items-center justify-between gap-2 px-5 py-2.5 text-sm" data-testid={`celebration-${i}`}>
                    <span className="text-[#0B1A2F] truncate flex items-center gap-2">{u.type === "Birthday" ? <Cake size={13} className="text-pink-400 shrink-0" /> : <PartyPopper size={13} className="text-amber-400 shrink-0" />}{u.title}
                      {milestone && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-700 border border-amber-300 px-2 py-0.5 text-[11px] font-semibold shrink-0" data-testid={`milestone-badge-${i}`}>
                          <Award size={11} />{u.years} yr{u.years === 1 ? "" : "s"}
                        </span>
                      )}
                    </span>
                    <span className="text-xs text-slate-400 shrink-0" data-testid={`celebration-date-${i}`}>{u.display || u.date}{u.type === "Work anniversary" && u.years != null ? ` (${u.years} ${u.years === 1 ? "year" : "years"})` : ""}</span>
                  </div>
                  );
                })}
              </div>
            </div>
          )}

          {d.probation?.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm" data-testid="home-probation">
              <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2"><ShieldCheck size={16} className="text-[#2EA3FD]" /><h2 className="font-semibold text-[#0B1A2F]">Probation tracker</h2><span className="text-xs bg-slate-100 text-slate-600 rounded-full px-2 py-0.5">{d.probation.length}</span></div>
              <div className="divide-y divide-slate-50 max-h-80 overflow-y-auto">
                {d.probation.map((p, i) => {
                  const dot = p.rag === "red" ? "bg-rose-500" : p.rag === "amber" ? "bg-amber-400" : "bg-emerald-500";
                  const when = p.days < 0 ? `${Math.abs(p.days)}d overdue` : p.days === 0 ? "due today" : `in ${p.days}d`;
                  return (
                    <Link key={i} to={p.to} className="flex items-center gap-3 px-5 py-2.5 hover:bg-slate-50" data-testid={`probation-item-${i}`}>
                      <span className={`h-2.5 w-2.5 rounded-full shrink-0 ${dot}`} data-testid={`probation-rag-${i}`} />
                      <div className="min-w-0 flex-1"><p className="text-sm font-semibold text-[#0B1A2F] truncate">{p.name}</p><p className="text-xs text-slate-400 truncate">{p.label} · {fmtDate(p.due)}</p></div>
                      <span className={`text-xs shrink-0 ${p.rag === "red" ? "text-rose-600" : p.rag === "amber" ? "text-amber-600" : "text-slate-400"}`}>{when}</span>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}

          {d.quick_actions?.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4" data-testid="home-quick-actions">
              <h2 className="font-semibold text-[#0B1A2F] flex items-center gap-2 mb-3 px-1"><Zap size={16} className="text-[#36776B]" />Quick access</h2>
              {d.quick_actions[0]?.sub ? (
                <div className="grid grid-cols-2 gap-3">
                  {d.quick_actions.map((q, i) => (
                    <Link key={i} to={q.to} data-testid={`quick-action-${i}`} className="rounded-lg border border-slate-200 p-3 hover:border-[#2EA3FD] hover:shadow-sm transition-all">
                      <div className="flex items-start gap-2"><ChevronRight size={15} className="text-[#2EA3FD] mt-0.5 shrink-0" /><div className="min-w-0"><p className="text-sm font-semibold text-[#0B1A2F]">{q.label}</p><p className="text-xs text-slate-400 leading-snug">{q.sub}</p></div></div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {d.quick_actions.map((q, i) => (<Button key={i} asChild size="sm" variant="outline" className="h-8"><Link to={q.to} data-testid={`quick-action-${i}`}>{q.label}</Link></Button>))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {isEmp && content?.values?.filter(Boolean).length > 0 && (
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-6" data-testid="home-cass-values">
          <div className="flex items-center gap-2 mb-4"><Award size={18} className="text-[#36776B]" /><h2 className="font-semibold text-[#0B1A2F]">Our CASS values</h2></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {content.values.filter(Boolean).map((v, i) => (
              <div key={i} data-testid={`cass-value-${i}`} className="rounded-xl border border-slate-100 bg-[#F5FAFF] px-4 py-3 flex items-center gap-2.5">
                <span className="h-7 w-7 rounded-full bg-[#2EA3FD]/15 text-[#2EA3FD] grid place-items-center text-xs font-bold shrink-0">{i + 1}</span>
                <span className="text-sm font-medium text-[#0B1A2F]">{v}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
